import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, AlertTriangle, WifiOff } from 'lucide-react';

const CandidateAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answeredSet, setAnsweredSet] = useState(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showIntegrityPrompt, setShowIntegrityPrompt] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const questionStartTime = useRef(Date.now());
  const assessmentIdRef = useRef(null);
  const lastPromptIndex = useRef(-10);
  const devtoolsCheckInterval = useRef(null);
  const currentIndexRef = useRef(0);
  const eventQueue = useRef([]);
  const flushTimer = useRef(null);

  // Keep ref in sync with state for use in event listeners
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  useEffect(() => { loadData(); }, [token]);

  // === Batched integrity event logging (debounced) ===
  const queueEvent = useCallback((type) => {
    if (!assessmentIdRef.current) return;
    eventQueue.current.push({ type, index: currentIndexRef.current, ts: Date.now() });

    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushEvents, 5000);
  }, []);

  const flushEvents = useCallback(async () => {
    if (eventQueue.current.length === 0) return;
    const batch = [...eventQueue.current];
    eventQueue.current = [];

    // Deduplicate — keep only latest per event type
    const deduped = {};
    batch.forEach(e => { deduped[e.type] = e; });

    for (const e of Object.values(deduped)) {
      try {
        await api.logIntegrityEvent({ assessment_id: assessmentIdRef.current, event_type: e.type, question_index: e.index });
      } catch {}
    }
  }, []);

  // Flush on unmount
  useEffect(() => () => { flushEvents(); if (flushTimer.current) clearTimeout(flushTimer.current); }, [flushEvents]);

  // === SECURITY: Integrity Monitoring ===
  useEffect(() => {
    const handleVisibility = () => { if (document.hidden && assessmentIdRef.current) queueEvent('tab_switch'); };
    const handleBlur = () => { if (assessmentIdRef.current) queueEvent('window_blur'); };
    const handleCopy = (e) => { e.preventDefault(); if (assessmentIdRef.current) { queueEvent('copy_attempt'); toast.warning('Copying is not permitted during the assessment.'); } };
    const handlePaste = (e) => { e.preventDefault(); if (assessmentIdRef.current) { queueEvent('paste_attempt'); toast.warning('Pasting is not permitted during the assessment.'); } };
    const handleCut = (e) => { e.preventDefault(); if (assessmentIdRef.current) queueEvent('cut_attempt'); };
    const handleContext = (e) => { e.preventDefault(); if (assessmentIdRef.current) queueEvent('right_click'); };

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 || e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
        if (assessmentIdRef.current) queueEvent('mouse_leave');
      }
    };

    const handleKeyDown = (e) => {
      if (!assessmentIdRef.current) return;
      const blocked = [
        e.key === 'PrintScreen',
        e.ctrlKey && e.key === 'c',
        e.ctrlKey && e.key === 'v',
        e.ctrlKey && e.key === 'a',
        e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i'),
        e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j'),
        e.ctrlKey && (e.key === 'u' || e.key === 'U'),
        e.key === 'F12',
        e.altKey && e.key === 'Tab',
      ];
      if (blocked.some(Boolean)) {
        e.preventDefault();
        e.stopPropagation();
        queueEvent('blocked_shortcut');
      }
    };

    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement;
      setIsFullscreen(isFull);
      if (!isFull && assessmentIdRef.current) queueEvent('fullscreen_exit');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);
    document.addEventListener('contextmenu', handleContext);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // DevTools check — reduced frequency to 10s
    devtoolsCheckInterval.current = setInterval(() => {
      const threshold = 160;
      if (window.outerWidth - window.innerWidth > threshold || window.outerHeight - window.innerHeight > threshold) {
        if (assessmentIdRef.current) queueEvent('devtools_open');
      }
    }, 10000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('contextmenu', handleContext);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (devtoolsCheckInterval.current) clearInterval(devtoolsCheckInterval.current);
    };
  }, [queueEvent]);

  // Periodic integrity check
  useEffect(() => {
    if (questions.length === 0) return;
    const gap = currentIndex - lastPromptIndex.current;
    if (gap >= 10 && currentIndex > 0 && currentIndex < questions.length - 1) {
      setShowIntegrityPrompt(true);
      lastPromptIndex.current = currentIndex;
    }
  }, [currentIndex, questions.length]);

  const handleIntegrityConfirm = () => {
    setShowIntegrityPrompt(false);
    try { api.logIntegrityEvent({ assessment_id: assessmentIdRef.current, event_type: 'integrity_check_passed', question_index: currentIndex }); } catch {}
  };

  const requestFullscreen = () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch {}
  };

  const loadData = async () => {
    try {
      const aRes = await api.getAssessmentByToken(token);
      setAssessment(aRes.data);
      assessmentIdRef.current = aRes.data.id;

      if (aRes.data.status === 'pending') {
        navigate(`/assess/${token}`, { replace: true });
        return;
      }

      // Log browser fingerprint (fire-and-forget)
      try {
        api.logIntegrityEvent({ assessment_id: aRes.data.id, event_type: 'browser_fingerprint', question_index: 0, metadata: {
          userAgent: navigator.userAgent, screen: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, language: navigator.language,
          platform: navigator.platform, cores: navigator.hardwareConcurrency || 'unknown',
        }});
      } catch {}

      const qRes = await api.getAssessmentQuestions(token);
      setQuestions(qRes.data.questions);
      const answered = new Set(qRes.data.answered || []);
      setAnsweredSet(answered);

      const firstUnanswered = qRes.data.questions.findIndex(q => !answered.has(q.q_number));
      if (firstUnanswered >= 0) {
        setCurrentIndex(firstUnanswered);
      } else if (answered.size >= qRes.data.questions.length) {
        navigate('/thank-you', { replace: true });
        return;
      }

      requestFullscreen();
      setConnectionError(false);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load assessment');
      setLoading(false);
    }
  };

  const handleSubmitAnswer = useCallback(async (option) => {
    if (submitting) return;
    setSubmitting(true);
    setConnectionError(false);
    const timeSpent = (Date.now() - questionStartTime.current) / 1000;
    const q = questions[currentIndex];

    let anomalyFlag = null;
    if (timeSpent < 3) anomalyFlag = 'suspiciously_fast';
    else if (timeSpent > 90) anomalyFlag = 'suspiciously_slow';

    // Retry logic — up to 3 attempts
    let attempts = 0;
    const maxAttempts = 3;
    while (attempts < maxAttempts) {
      try {
        await api.saveResponse({
          assessment_id: assessmentIdRef.current,
          q_number: q.q_number,
          selected_option: option,
          time_spent_seconds: timeSpent,
          anomaly_flag: anomalyFlag,
        });

        setAnsweredSet(prev => new Set([...prev, q.q_number]));
        setConnectionError(false);

        if (currentIndex < questions.length - 1) {
          setCurrentIndex(currentIndex + 1);
          setSelectedOption(null);
          questionStartTime.current = Date.now();
        } else {
          if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
          navigate('/thank-you', { replace: true });
        }
        setSubmitting(false);
        return; // Success — exit
      } catch (err) {
        attempts++;
        if (attempts < maxAttempts) {
          // Wait before retry (1s, 2s)
          await new Promise(r => setTimeout(r, attempts * 1000));
        }
      }
    }

    // All retries failed
    setSubmitting(false);
    setConnectionError(true);
    toast.error('Connection issue — your answer was not saved. Please check your internet and try again.');
  }, [currentIndex, questions, submitting, navigate]);

  // Auto-recovery: reset submitting after 15s safety net
  useEffect(() => {
    if (!submitting) return;
    const safety = setTimeout(() => {
      setSubmitting(false);
      setConnectionError(true);
    }, 15000);
    return () => clearTimeout(safety);
  }, [submitting]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mx-auto mb-4" />
          <p className="text-[#5C665F] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6] p-4">
        <div className="text-center bg-white p-8 rounded-none shadow-sm border border-black/5 max-w-md">
          <AlertTriangle className="h-10 w-10 text-accent2 mx-auto mb-3" />
          <h2 className="text-xl font-medium text-[#1A231C] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Assessment Unavailable</h2>
          <p className="text-[#5C665F] text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-4 text-sm text-brand underline underline-offset-2" data-testid="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!questions.length) return null;

  const currentQ = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLikert = currentQ.q_type === 'likert';
  const likertLabels = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

  return (
    <div className="min-h-screen bg-[#F9F9F6] relative select-none" data-testid="assessment-page" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Progress + integrity banner */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="h-1.5 bg-[#E5E7EB]">
          <motion.div
            className="h-full bg-brand"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            data-testid="progress-bar"
          />
        </div>
        <div className="bg-brand text-white/80 flex items-center justify-between px-4 py-1.5 text-[11px]" data-testid="integrity-banner">
          <div className="flex items-center gap-1.5">
            <Shield className="h-3 w-3" />
            <span>This session is monitored for integrity</span>
          </div>
          <span className="font-mono text-white/60">{currentIndex + 1} / {questions.length}</span>
        </div>
      </div>

      {/* Connection error banner */}
      {connectionError && (
        <div className="fixed top-12 left-0 right-0 z-40 bg-accent2 text-white text-xs text-center py-2 flex items-center justify-center gap-2" data-testid="connection-error-banner">
          <WifiOff className="h-3.5 w-3.5" />
          <span>Connection issue detected. Please check your internet and try again.</span>
        </div>
      )}

      {/* Fullscreen prompt */}
      {!isFullscreen && !loading && (
        <div className="fixed bottom-4 right-4 z-40 bg-white border border-accent2/30 rounded-none p-3 shadow-lg max-w-xs" data-testid="fullscreen-prompt">
          <p className="text-xs text-[#1A231C] mb-2 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-accent2" />
            Fullscreen recommended for best experience
          </p>
          <button onClick={requestFullscreen} className="text-xs font-medium text-brand underline underline-offset-2 hover:text-brand-dark" data-testid="re-enter-fullscreen">
            Enter Fullscreen
          </button>
        </div>
      )}

      {/* Integrity check modal */}
      <AnimatePresence>
        {showIntegrityPrompt && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
            data-testid="integrity-check-modal"
          >
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              className="bg-white p-8 rounded-none shadow-2xl max-w-sm text-center">
              <Shield className="h-10 w-10 text-brand mx-auto mb-3" />
              <h3 className="text-lg font-medium text-[#1A231C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Integrity Check</h3>
              <p className="text-sm text-[#5C665F] mb-5">Please confirm you are completing this assessment independently.</p>
              <button onClick={handleIntegrityConfirm}
                className="bg-brand text-white px-8 py-3 text-sm font-medium hover:bg-brand-dark transition-colors rounded-none w-full"
                data-testid="integrity-confirm-button">
                I Confirm — Continue Assessment
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="min-h-screen flex items-center justify-center p-4 pt-16">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-none shadow-sm border border-black/5 p-8 md:p-10"
            >
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C665F] mb-4" data-testid="question-number">
                Question {currentIndex + 1}
              </p>

              <h2 className="text-xl sm:text-2xl font-medium text-[#1A231C] mb-8 leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="question-text">
                {currentQ.text}
              </h2>

              {isLikert ? (
                <div className="mb-8" data-testid="likert-container">
                  <div className="flex flex-col sm:flex-row gap-3 w-full justify-between">
                    {['1', '2', '3', '4', '5'].map((val, idx) => (
                      <button key={val} onClick={() => setSelectedOption(val)}
                        className={`flex-1 min-h-[72px] p-3 border-2 transition-all duration-200 flex flex-col items-center justify-center gap-1 rounded-none ${
                          selectedOption === val
                            ? 'bg-brand border-brand text-white'
                            : 'bg-white border-[#E5E7EB] text-[#1A231C] hover:border-brand/40'
                        }`}
                        data-testid={`likert-option-${val}`}>
                        <span className="text-lg font-bold">{val}</span>
                        <span className="text-[10px] leading-tight opacity-80">{likertLabels[idx]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mb-8">
                  {currentQ.options && Object.entries(currentQ.options).map(([key, text]) => {
                    const isSelected = selectedOption === key;
                    return (
                      <button key={key} onClick={() => setSelectedOption(key)}
                        className={`w-full min-h-[56px] p-4 border-2 rounded-none text-left transition-all duration-200 flex items-center gap-3 ${
                          isSelected ? 'bg-brand border-brand text-white' : 'border-[#E5E7EB] hover:border-brand/40 text-[#1A231C]'
                        }`}
                        data-testid={`answer-option-${key}`}>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-white bg-white' : 'border-[#5C665F]/40'
                        }`}>
                          {isSelected && <div className="w-2 h-2 bg-brand rounded-full" />}
                        </div>
                        <span className="text-sm leading-relaxed">{text}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <Button
                onClick={() => handleSubmitAnswer(selectedOption)}
                disabled={!selectedOption || submitting}
                className="w-full bg-brand hover:bg-brand-dark text-white rounded-none h-12 font-medium disabled:opacity-30 transition-colors"
                data-testid="next-question-button"
              >
                {submitting ? 'Saving...' : currentIndex < questions.length - 1 ? 'Next Question' : 'Submit Assessment'}
              </Button>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CandidateAssessment;
